import { analyseListing as analyseListingApi } from './api';
// Only a fallback: the merged runtime configuration is what the caller passes
// in, and it is the one Console updates. See buildEnums.
import { listingFields as localListingFields } from '../config/configListing';
import { categories as localCategories } from '../config/configCategories';

/**
 * Listing AI — client-side facade for the server `/api/analyse-listing`
 * endpoint, which calls Claude vision and returns a structured set of
 * suggested fields for the Edit-Listing Details tab.
 *
 * Response shape (matches the schema enforced in
 * `server/api/analyse-listing.js`):
 *   {
 *     fields: {
 *       title:        string,
 *       description:  string,
 *       publicData: { categoryLevel1, categoryLevel2, merke,
 *                     'storrelse-klar', farge, materiale, passform,
 *                     tilstand },
 *     },
 *     confidences: Record<string, number>,
 *   }
 *
 * Confidence is a 0..1 score. The Confirm Details panel renders a
 * yellow "needs review" dot next to any field with confidence below
 * [[CONFIDENCE_THRESHOLD]] so the seller knows to double-check it.
 */

// Image variants we ask the model to look at. The EditListing flow
// requests `listing-card` (400px) + `listing-card-2x` (800px) at upload
// time, so those are what's reliably present on the form's `images`
// array. We prefer the 2x variant so Claude can read brand labels.
// The `scaled-*` fallbacks cover edge cases (e.g. a Sharetribe SDK
// configured to also fetch the larger variants on an existing listing).
const PREFERRED_VARIANTS = [
  'scaled-medium',
  'scaled-small',
  'listing-card-2x',
  'listing-card',
  'default',
];

const pickImageUrl = image => {
  const variants = image?.attributes?.variants || {};
  for (const name of PREFERRED_VARIANTS) {
    const url = variants[name]?.url;
    if (typeof url === 'string' && url.startsWith('http')) return url;
  }
  // Last-ditch fallback: scan any variant for a URL. Covers cases where
  // a marketplace is configured with a different variant prefix.
  for (const v of Object.values(variants)) {
    if (typeof v?.url === 'string' && v.url.startsWith('http')) return v.url;
  }
  return null;
};

/**
 * How long the phone waits for the AI before giving up. On busy mobile data at
 * Guro's Zuprize Studio event (31 Oct) an answer could be lost on the way and
 * the spinner never stopped. The server answers within its own 25-second
 * budget (server/api/analyse-listing.js), so a wait this long means the
 * connection has gone quiet.
 */
export const AI_TIMEOUT_MS = 25000;

const extractImageUrls = images => {
  if (!Array.isArray(images)) return [];
  // Cap at 6 — Claude can handle more but cost scales and the marginal
  // signal from photo #7 is tiny for a clothing listing.
  return images
    .map(pickImageUrl)
    .filter(Boolean)
    .slice(0, 6);
};

// Fields we want Claude to choose from. The shape matches what the
// server prompt expects — see `server/api/analyse-listing.js`.
const WANT_FIELDS = ['merke', 'storrelse-klar', 'farge', 'materiale', 'passform', 'tilstand'];

/**
 * Pull enum options out of the listing configuration so the server prompt can
 * constrain Claude to existing option keys.
 *
 * This takes the *merged* configuration, hosted over local, because the brand
 * list lives in Console and Guro adds to it. Reading the local
 * `configListing.js` here instead meant every brand added since that file was
 * written was invisible to the model, which then did as the prompt says for a
 * brand it cannot match — picked "annet" and wrote the name into the free-text
 * field. A brand could therefore be in the filter and still produce listings
 * filed under "Annet" (Guro, Sept 2026).
 *
 * The local config stays as the fallback, for a cold start where the hosted
 * assets have not landed yet.
 *
 * @param {Object} [listingConfig]
 * @param {Array} [listingConfig.listingFields] - `config.listing.listingFields`
 * @param {Array} [listingConfig.categories] - `config.categoryConfiguration.categories`
 */
const buildEnums = ({ listingFields, categories } = {}) => {
  const fields = listingFields?.length ? listingFields : localListingFields;
  const configCategories = categories?.length ? categories : localCategories;
  const enums = {};
  for (const f of fields || []) {
    if (!WANT_FIELDS.includes(f.key)) continue;
    // An option for some categories only (the waist sizes for trousers) says
    // which, so the model doesn't put W28 on a dress.
    const options = (f.enumOptions || []).map(o => ({
      key: o.option,
      label: o.label || o.option,
      ...(o.categoryIds ? { categoryIds: o.categoryIds } : {}),
    }));
    if (options.length) enums[f.key] = options;
  }
  if (Array.isArray(configCategories) && configCategories.length) {
    // Walk the full category tree so the model can pick
    // categoryLevel1 → categoryLevel2 → categoryLevel3 from valid
    // parent/child chains (e.g. klar → klar-overdeler →
    // klar-overdeler-bluser).
    // Hidden categories can't be chosen in the form, so they aren't offered.
    const walk = nodes =>
      (nodes || [])
        .filter(n => !n.hidden)
        .map(n => ({
          key: n.id,
          label: n.name,
          children: walk(n.subcategories),
        }));
    enums.categoryLevel1 = walk(configCategories);
  }
  return enums;
};

/**
 * Analyse a set of listing images and return a suggested set of
 * listing fields. POSTs to the server-side endpoint that calls Claude.
 *
 * @param {Object} args
 * @param {Array<Object>} args.images - Uploaded image entities. Each
 *   should have `attributes.variants.{name}.url`. Images still in
 *   flight (no server entity yet) are skipped.
 * @param {Object} args.listingConfig - `{ listingFields, categories }` from
 *   the merged runtime configuration, so the prompt is constrained to the
 *   enum keys that actually exist today rather than the local fallback.
 * @returns {Promise<{fields, confidences}>}
 */
export const analyzeListing = async ({ images, listingConfig } = {}) => {
  const imageUrls = extractImageUrls(images);
  if (!imageUrls.length) {
    throw new Error('analyzeListing: no image URLs to analyse');
  }
  const enums = buildEnums(listingConfig);

  // The phone stops waiting after AI_TIMEOUT_MS, and the seller moves on to
  // fill in the details herself (code 'ai-timeout'). The race, not only the
  // abort, is what ends the wait: an aborted request on a stalled mobile
  // connection is not guaranteed to settle promptly in every browser.
  const controller = typeof AbortController === 'undefined' ? null : new AbortController();
  let timer = null;
  const timedOut = new Promise((resolve, reject) => {
    timer = setTimeout(() => {
      if (controller) {
        controller.abort();
      }
      const error = new Error('analyse-listing timed out');
      error.code = 'ai-timeout';
      reject(error);
    }, AI_TIMEOUT_MS);
  });
  try {
    const request = analyseListingApi(
      { imageUrls, enums },
      controller ? { signal: controller.signal } : {}
    );
    const data = await Promise.race([request, timedOut]);
    if (!data?.suggestion) {
      throw new Error('analyse-listing returned an empty payload');
    }
    return data.suggestion;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Confidence threshold below which the Confirm Details panel renders
 * the yellow "needs review" dot next to a field.
 */
export const CONFIDENCE_THRESHOLD = 0.7;
